import { useState, useEffect } from 'react';

// Mobile-first breakpoints (realistic for actual devices)
export const BREAKPOINTS = {
  mobile: 768, // Typical mobile phones (including landscape)
  tablet: 1024, // Tablets
  desktop: 1200 // Desktop and larger
};

// Minimized / occluded browser windows often report 0x0 or a handful of pixels.
const MIN_PLAUSIBLE_VIEWPORT = 200;

export const classifyViewport = (width) => {
  if (width < BREAKPOINTS.mobile) {
    return { screenSize: 'mobile', isMobile: true, isTablet: false, isDesktop: false };
  }
  if (width < BREAKPOINTS.tablet) {
    return { screenSize: 'tablet', isMobile: false, isTablet: true, isDesktop: false };
  }
  return { screenSize: 'desktop', isMobile: false, isTablet: false, isDesktop: true };
};

// Laptops and monitors stay on the desktop layout even when the window is
// minimized or resized. Phones and most tablets still follow the viewport.
export const isDesktopClassDevice = (screen) => {
  if (!screen || typeof screen.width !== 'number' || typeof screen.height !== 'number') {
    return false;
  }
  const longEdge = Math.max(screen.width, screen.height);
  const shortEdge = Math.min(screen.width, screen.height);
  return longEdge >= 1280 && shortEdge >= 720;
};

export const shouldIgnoreViewportUpdate = ({ width, height, visibilityState }) => {
  if (visibilityState === 'hidden') {
    return true;
  }
  if (!width || !height || width < MIN_PLAUSIBLE_VIEWPORT) {
    return true;
  }
  return false;
};

export const resolveLayoutState = ({
  width,
  height,
  visibilityState,
  screen,
  previousState
}) => {
  if (isDesktopClassDevice(screen)) {
    return { ...classifyViewport(BREAKPOINTS.tablet), width };
  }

  if (shouldIgnoreViewportUpdate({ width, height, visibilityState })) {
    if (previousState) {
      return previousState;
    }
    return { ...classifyViewport(BREAKPOINTS.tablet), width: width || BREAKPOINTS.tablet };
  }

  return { ...classifyViewport(width), width };
};

const getInitialState = () => {
  if (typeof window === 'undefined') {
    return { ...classifyViewport(BREAKPOINTS.tablet), width: BREAKPOINTS.tablet };
  }

  return resolveLayoutState({
    width: window.innerWidth,
    height: window.innerHeight,
    visibilityState: document.visibilityState,
    screen: window.screen,
    previousState: null
  });
};

const statesEqual = (a, b) => (
  a.screenSize === b.screenSize &&
  a.isMobile === b.isMobile &&
  a.isTablet === b.isTablet &&
  a.isDesktop === b.isDesktop
);

export const useResponsive = () => {
  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    const apply = () => {
      setState((prev) => {
        const next = resolveLayoutState({
          width: window.innerWidth,
          height: window.innerHeight,
          visibilityState: document.visibilityState,
          screen: window.screen,
          previousState: prev
        });
        return statesEqual(prev, next) ? prev : next;
      });
    };

    const onOrientationChange = () => {
      window.setTimeout(apply, 100);
    };

    apply();
    window.addEventListener('resize', apply);
    document.addEventListener('visibilitychange', apply);
    window.addEventListener('orientationchange', onOrientationChange);

    return () => {
      window.removeEventListener('resize', apply);
      document.removeEventListener('visibilitychange', apply);
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, []);

  return state;
};

export const useTouchDevice = () => {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    const checkTouch = () => {
      setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };

    checkTouch();
  }, []);

  return isTouch;
};
